/**
 * Sewing Efficiency — chart-only rollup of line rows to Building–Floor bars.
 *
 * Presentation over a complete result (CLAUDE.md §4.3, DECISIONS.md §31). Table and export stay
 * on line grain. Additive measures sum; efficiency is SUM(earned)/SUM(worked) from columns already
 * on each line row — never the average of line efficiencies.
 *
 * Caller must pass only complete, already-chipped (and otherwise chart-scoped) summary rows.
 */

import { plottedFields, type SewingRow } from './columns';
import type { ChartMode, Metric } from './filters';
import { buildingFloorValue } from './spotlight-state';

/** One floor bar for the summary chart: key matches Building–Floor spotlight encoding. */
export interface FloorChartBar {
  /** `buildingFloorValue` — what a bar click writes into `spotlight`. */
  key: string;
  /** Axis label, e.g. `K4-F2`. */
  label: string;
  /** Display units: pieces as-is, efficiency already ×100 (same as line chart after scale). */
  actual: number | null;
  target: number | null;
}

function toNumber(value: unknown): number | null {
  if (value === null || value === undefined || value === '') return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

/** Sum a numeric field; null if no row contributed a number. */
function sumField(rows: readonly SewingRow[], field: string): number | null {
  let total = 0;
  let saw = false;
  for (const row of rows) {
    const n = toNumber(row[field]);
    if (n === null) continue;
    total += n;
    saw = true;
  }
  return saw ? total : null;
}

/** Ratio of two sums; null when either leg is missing or the denominator is not positive. */
function ratioOfSums(numerator: number | null, denominator: number | null): number | null {
  if (numerator === null || denominator === null || denominator <= 0) return null;
  return numerator / denominator;
}

/**
 * Earned / worked column pair for the active metric and PPA toggle. Mirrors
 * {@link efficiencyField} but returns the ratio inputs, not the precomputed line efficiency.
 */
function efficiencyParts(
  metric: Metric,
  includePpa: boolean,
  available: ReadonlySet<string>,
): { earned: string; worked: string } {
  if (metric === 'EINS') {
    if (includePpa && available.has('eins_ppa_efficiency')) {
      return { earned: 'eins_ppa_output_smv', worked: 'eins_ppa_mo_work_time' };
    }
    return { earned: 'eins_output_smv', worked: 'eins_mo_work_time' };
  }
  if (includePpa && available.has('sfts_ppa_efficiency')) {
    return { earned: 'sfts_ppa_output_smv', worked: 'sfts_ppa_mo_work_time' };
  }
  return { earned: 'sfts_output_smv', worked: 'sfts_mo_work_time' };
}

interface FloorBucket {
  key: string;
  label: string;
  rows: SewingRow[];
}

function groupByBuildingFloor(rows: readonly SewingRow[]): FloorBucket[] {
  const map = new Map<string, FloorBucket>();
  for (const row of rows) {
    const building = String(row['building'] ?? '');
    const floor = String(row['floor'] ?? '');
    if (building === '' || floor === '') continue;
    const key = buildingFloorValue({ building, floor });
    const existing = map.get(key);
    if (existing !== undefined) {
      existing.rows.push(row);
    } else {
      map.set(key, { key, label: `${building}-${floor}`, rows: [row] });
    }
  }
  // Insertion order follows the first time each floor appears in the server/chip order; then sort
  // by label so the axis is stable and scannable (same idea as line name sort on the line chart).
  return [...map.values()].sort((a, b) => a.label.localeCompare(b.label));
}

/**
 * Roll line rows up to one bar per Building–Floor. Values are in chart display units (efficiency
 * already scaled to percent points) so the chart does not re-scale floor bars.
 */
export function rollupFloorBars(
  rows: readonly SewingRow[],
  mode: ChartMode,
  metric: Metric,
  includePpa: boolean,
  available: ReadonlySet<string>,
): FloorChartBar[] {
  const fields = plottedFields(mode, metric, includePpa, available);
  const buckets = groupByBuildingFloor(rows);

  if (mode === 'output') {
    return buckets.map((bucket) => ({
      key: bucket.key,
      label: bucket.label,
      actual: sumField(bucket.rows, fields.actual),
      target: sumField(bucket.rows, fields.target),
    }));
  }

  const parts = efficiencyParts(metric, includePpa, available);
  return buckets.map((bucket) => {
    const actual = ratioOfSums(sumField(bucket.rows, parts.earned), sumField(bucket.rows, parts.worked));
    const target = ratioOfSums(
      sumField(bucket.rows, 'target_output_smv'),
      sumField(bucket.rows, 'target_mo_work_mins'),
    );
    return {
      key: bucket.key,
      label: bucket.label,
      actual: actual === null ? null : actual * 100,
      target: target === null ? null : target * 100,
    };
  });
}
